import { useState, useEffect } from 'react';
import { Failure, FailureFormData } from '../models/failure';
import { fetchFailuresWithPagination, createFailure, updateFailure, deleteFailure, fetchFailureVideo } from '../api/failure';
import { useSelector } from 'react-redux';
import { getUserProfile } from '../configuration/login/loginSlice';
import { fetchUserId } from '../api/fetchUser';

export const useFailures = () => {
  const userProfile = useSelector(getUserProfile);

  const [failures, setFailures] = useState<Failure[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingFailure, setEditingFailure] = useState<Failure | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [failureToDelete, setFailureToDelete] = useState<Failure | null>(null);

  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);

  const loadFailures = async (page: number = 0, size: number = 10) => {
    setLoading(true);
    setError(null);
    try {
      const userId = await fetchUserId(userProfile);
      if (!userId) {
        setUserHasAccess(false);
        return;
      }

      const authorizedUserIds = import.meta.env.VITE_BACKOFFICE_AUTHORIZED_USER_IDS?.split(',').map((id: string) => id.trim()) || [];
      const isAuthorized = authorizedUserIds.includes(userId);
      if (!isAuthorized) {
        setUserHasAccess(false);
        return;
      }

      setUserHasAccess(true);

      const paginationData = await fetchFailuresWithPagination({ page, limit: size });
      
      if (page === 0) {
        setFailures(paginationData.content);
        setCurrentPage(0);
      } else {
        setFailures(prev => [...prev, ...paginationData.content]);
      }

      setTotalElements(paginationData.totalElements);
      setTotalPages(paginationData.totalPages);
      setHasMoreData(!paginationData.last);
    } catch (err) {
      setError('Erro ao carregar falhas');
      console.error('Error loading failures:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreFailures = async () => {
    if (!hasMoreData || loading) return;
    
    const nextPage = currentPage + 1;
    await loadFailures(nextPage, pageSize);
    setCurrentPage(nextPage);
  };

  const addFailure = async (title: string, description: string, videoFile: File): Promise<boolean> => {
    setError(null);
    
    try {
      const newFailure = await createFailure(title, description, videoFile);
      if (newFailure) {
        await loadFailures(0, pageSize);
        return true;
      }
      return false;
    } catch (err) {
      setError('Erro ao criar falha');
      console.error('Error creating failure:', err);
      return false;
    }
  };

  const editFailure = async (id: number, title: string, description: string, videoFile?: File): Promise<boolean> => {
    setError(null);
    
    try {
      const updatedFailure = await updateFailure(id, title, description, videoFile);
      if (updatedFailure) {
        setFailures(failures.map(failure => failure.id === id ? updatedFailure : failure));
        return true;
      }
      return false;
    } catch (err) {
      setError('Erro ao atualizar falha');
      console.error('Error updating failure:', err);
      return false;
    }
  };

  const removeFailure = async (id: number): Promise<boolean> => {
    setError(null);
    setDeleteLoading(true);
    try {
      const success = await deleteFailure(id);
      if (success) {
        setFailures(failures.filter(failure => failure.id !== id));
        return true;
      }
      return false;
    } catch (err) {
      setError('Erro ao excluir falha');
      console.error('Error deleting failure:', err);
      return false;
    } finally {
      setDeleteLoading(false);
    }
  };

  const getFailureVideo = async (id: number) => {
    try {
      const video = await fetchFailureVideo(id);
      return video;
    } catch (err) {
      console.error('Error fetching failure video:', err);
      return null;
    }
  };

  useEffect(() => {
    loadFailures(0, pageSize);
  }, []);

  const handleEdit = (failure: Failure) => {
    setEditingFailure(failure);
    setShowForm(true);
  };

  const handleDelete = (failure: Failure) => {
    setFailureToDelete(failure);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (failureToDelete?.id) {
      await removeFailure(failureToDelete.id);
      setShowDeleteDialog(false);
      setFailureToDelete(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingFailure(null);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingFailure(null);
  };

  return {
    failures,
    loading,
    deleteLoading,
    error,
    loadFailures,
    loadMoreFailures,
    addFailure,
    editFailure,
    removeFailure,
    getFailureVideo,
    showForm,
    editingFailure,
    showDeleteDialog,
    failureToDelete,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    userHasAccess,
    currentPage,
    totalElements,
    totalPages,
    hasMoreData,
    pageSize,
  };
};
